import { logger } from '../monitoring/sentry';

const TRANSIENT_NETWORK_PATTERNS = [
  'failed to fetch',
  'load failed',
  'networkerror',
  'network request failed',
  'a network error has occurred',
  'the internet connection appears to be offline',
  'the network connection was lost',
  'a server with the specified hostname could not be found',
];

const createAbortError = () => {
  if (typeof DOMException === 'undefined') {
    const error = new Error('Aborted');
    error.name = 'AbortError';
    return error;
  }

  return new DOMException('Aborted', 'AbortError');
};

const throwIfAborted = (signal) => {
  if (signal?.aborted) {
    throw createAbortError();
  }
};

export const isTransientNetworkError = (error) => {
  if (error?.isTransientNetworkError) {
    return true;
  }

  const checkMessage = (msg) => {
    const lowerMsg = (msg ?? '').toLowerCase();
    return TRANSIENT_NETWORK_PATTERNS.some((pattern) => lowerMsg.includes(pattern));
  };

  // Check main error message
  if (checkMessage(error?.message)) {
    return true;
  }

  // Check error cause chain for wrapped errors (e.g., Amplify-wrapped network errors)
  let cause = error?.cause;
  let depth = 0;
  while (cause && depth < 10) {
    if (cause?.isTransientNetworkError) {
      return true;
    }

    if (checkMessage(cause?.message)) {
      return true;
    }
    cause = cause?.cause;
    depth++;
  }

  return false;
};

const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    throwIfAborted(signal);

    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);

    const onAbort = () => {
      clearTimeout(timer);
      cleanup();
      reject(createAbortError());
    };

    const cleanup = () => {
      signal?.removeEventListener?.('abort', onAbort);
    };

    signal?.addEventListener?.('abort', onAbort);
  });

export const withRetry = async (
  fn,
  { label = 'request', maxAttempts = 3, baseDelay = 500, signal } = {}
) => {
  let lastError;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      throwIfAborted(signal);
      return await fn();
    } catch (error) {
      lastError = error;
      if (error?.name === 'AbortError') {
        throw error;
      }
      if (attempt < maxAttempts && isTransientNetworkError(error)) {
        const delay = baseDelay * 2 ** (attempt - 1);
        logger.warn(`${label}: transient network error, retrying (${attempt}/${maxAttempts})`, {
          attempt,
          delay,
          message: error.message,
        });
        await wait(delay, signal);
        continue;
      }
      throw error;
    }
  }
  throw lastError;
};
